/**
 * Server-sent events over `fetch`, because `EventSource` cannot carry a token.
 *
 * The browser's EventSource API sends no request headers, so once the engine
 * began verifying tokens every log stream was refused with a 401 — the request
 * arrived anonymous. There is no way to attach an `Authorization` header to an
 * EventSource, and the alternatives are worse: a token in the query string
 * lands in access logs, referrer headers and browser history, and exempting the
 * path re-opens the endpoint a penetration test reported.
 *
 * So the stream is read with `fetch`, which can set headers. What that costs is
 * the reconnect behaviour EventSource gave for free, re-implemented below.
 */

/** One SSE frame's payload, plus whatever is left mid-frame. */
export function parseFrames(buffer: string): {
  events: string[];
  rest: string;
} {
  const events: string[] = [];
  // SSE permits CRLF, and an intermediary may rewrite to it. Normalising up
  // front keeps the frame split working and the payload free of stray carriage
  // returns; splitting on "\n\n" alone against a CRLF stream yields no frames
  // at all, and the log view would sit empty with nothing logged anywhere.
  let rest = buffer.replace(/\r\n/g, "\n");

  // Frames end at a blank line. Anything after the last one is a partial
  // frame: a chunk boundary can fall mid-JSON, and emitting half an object
  // would drop the entry with no error anywhere.
  let split = rest.indexOf("\n\n");
  while (split !== -1) {
    const frame = rest.slice(0, split);
    rest = rest.slice(split + 2);

    const payload = frame
      .split("\n")
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.slice(5).replace(/^ /, ""))
      .join("\n");

    // Comment frames (`: keep-alive`) carry no data line and are skipped —
    // parsed as data they would show up as blank rows in the log view.
    if (payload) events.push(payload);
    split = rest.indexOf("\n\n");
  }

  return { events, rest };
}

/**
 * Whether a status means "stop asking" rather than "try again".
 *
 * Only the authentication failures. Retrying those repeats the same rejection
 * and turns one refused request into a storm against the endpoint that just
 * declined it; the caller has to re-authenticate before anything changes.
 *
 * Everything else — 500, 502, 503, 429 — is a server that may well be back in
 * a moment, so the stream reconnects. Treating them as terminal killed the
 * stream permanently on a restarting engine, and left the view on "connecting"
 * forever because the badge rule counts them as transient.
 */
function isRefusal(status: number): boolean {
  return status === 401 || status === 403;
}

export interface LogStreamOptions {
  token: string | null;
  onOpen?: () => void;
  onMessage?: (data: string) => void;
  /** Called when the stream ends without being closed by the caller. */
  onError?: (error: { status?: number }) => void;
  /** Delay before reconnecting. Zero disables the wait (tests). */
  retryMs?: number;
  fetchImpl?: typeof fetch;
}

export interface LogStream {
  close: () => void;
  /** Resolves when the current attempt has finished — for tests. */
  settled: Promise<void>;
}

export function streamLogs(url: string, options: LogStreamOptions): LogStream {
  const {
    token,
    onOpen,
    onMessage,
    onError,
    retryMs = 3000,
    fetchImpl = fetch,
  } = options;

  const controller = new AbortController();
  let closed = false;
  let retry: ReturnType<typeof setTimeout> | null = null;

  /** Handlers must never fire after the caller has closed: a late `onOpen`
   *  flips a paused view back to live. */
  const report = (fire: () => void) => {
    if (!closed && !controller.signal.aborted) fire();
  };

  const pump = async (reader: ReadableStreamDefaultReader<Uint8Array>) => {
    const decoder = new TextDecoder();
    let buffer = "";

    while (!closed) {
      const { done, value } = await reader.read();
      if (done) return;

      buffer += decoder.decode(value, { stream: true });
      const { events, rest } = parseFrames(buffer);
      buffer = rest;
      for (const event of events) report(() => onMessage?.(event));
    }
  };

  /** One connection, start to finish. `refused` means do not come back. */
  const attempt = async (): Promise<"refused" | "ended"> => {
    const response = await fetchImpl(url, {
      headers: {
        Accept: "text/event-stream",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      report(() => onError?.({ status: response.status }));
      return isRefusal(response.status) ? "refused" : "ended";
    }

    const reader = response.body?.getReader();
    if (!reader) {
      // A 200 with no body is a transport failure, not a quiet success —
      // returning silently would leave the view "connecting" forever.
      report(() => onError?.({}));
      return "ended";
    }

    report(() => onOpen?.());
    await pump(reader);
    return "ended";
  };

  /** Whether to come back after an attempt.
   *
   * EventSource reconnected unconditionally; this does not. A refusal is
   * final — asking again just repeats it — a closed stream is finished, and a
   * zero delay means the caller wanted a single attempt.
   */
  const shouldReconnect = (outcome: "refused" | "ended") =>
    outcome === "ended" && !closed && retryMs > 0;

  const run = async (): Promise<void> => {
    let outcome: "refused" | "ended" = "ended";
    try {
      outcome = await attempt();
    } catch {
      // Transport failure rather than a refusal: no status to report, and the
      // reconnect below is the right response.
      report(() => onError?.({}));
    }

    if (shouldReconnect(outcome)) {
      retry = setTimeout(() => {
        if (!closed) void run();
      }, retryMs);
    }
  };

  const settled = run();

  return {
    close: () => {
      closed = true;
      if (retry) clearTimeout(retry);
      controller.abort();
    },
    settled,
  };
}
